import ErrorBoundary from '@/components/ErrorBoundary';
import ElleLogo from '@/elle/public/image/elle-logo.svg';
import { DefaultComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { useAppState } from 'lib/hooks/useAppState';
import { useComponentsState } from 'lib/hooks/useComponentsState';
import mergeProps from 'lib/utils/mergeProps';
import { ReactNode } from 'react';
import { FrontCoverstory, FrontCoverstoryProps } from './FrontCoverstory';
import { FrontCoverstoryCaptionProps } from './FrontCoverstory.Caption';
import { FrontCoverstoryDescriptionProps } from './FrontCoverstory.Description';
import { FrontCoverstoryGroupProps } from './FrontCoverstory.Group';
import { FrontCoverstoryImageProps } from './FrontCoverstory.Image';
import { FrontCoverstoryLogoProps } from './FrontCoverstory.Logo';
import { FrontCoverstoryVideoProps } from './FrontCoverstory.Video';

export interface StandaloneFrontCoverstoryProps extends StandaloneComponentProps {
  link?: FrontCoverstoryProps;
  title?: string;
  subtitle?: string;
  mediaUrl?: string;
  logo?: ReactNode;
  options?: FrontCoverstoryProps['options'] & {
    $caption?: FrontCoverstoryCaptionProps;
    $description?: FrontCoverstoryDescriptionProps;
    $group?: FrontCoverstoryGroupProps;
    $image?: FrontCoverstoryImageProps;
    $video?: FrontCoverstoryVideoProps;
    $logo?: FrontCoverstoryLogoProps;
  };
}

export const StandaloneFrontCoverstory: StandaloneComponent<StandaloneFrontCoverstoryProps> = ({
  link,
  title,
  subtitle,
  mediaUrl,
  logo,
  options,
}) => {
  const {
    $caption: captionProps,
    $description: descriptionProps,
    $group: groupProps,
    $image,
    $video: videoProps,
    $logo: logoProps,
    ...baseProps
  } = options ?? {};

  const { $standalone: imageStandaloneProps, ...imageProps } = $image ?? {};

  const {
    Navigation: {
      $drawer: { isVisible: isMenuDrawerOpened },
    },
  } = useComponentsState();

  return (
    <ErrorBoundary>
      <FrontCoverstory
        data-hidden={isMenuDrawerOpened}
        {...mergeProps({ options: baseProps }, link)}
        content={
          <>
            {mediaUrl?.includes('mp4') ? (
              <FrontCoverstory.Video videoURL={mediaUrl} data-hidden={isMenuDrawerOpened} {...videoProps} />
            ) : (
              <FrontCoverstory.Image
                $standalone={{ src: mediaUrl, alt: title, ...imageStandaloneProps }}
                data-hidden={isMenuDrawerOpened}
                {...imageProps}
              />
            )}
            {logo && (
              <FrontCoverstory.Logo data-hidden={isMenuDrawerOpened} {...logoProps}>
                {logo}
              </FrontCoverstory.Logo>
            )}
            <FrontCoverstory.Group {...groupProps}>
              {title && <FrontCoverstory.Caption {...captionProps}>{title}</FrontCoverstory.Caption>}
              {subtitle && <FrontCoverstory.Description {...descriptionProps}>{subtitle}</FrontCoverstory.Description>}
            </FrontCoverstory.Group>
          </>
        }
      />
    </ErrorBoundary>
  );
};

const DefaultFrontCoverstory: DefaultComponent = ({ type, meta, data, descendants }) => {
  const [{ device }] = useAppState();

  return (
    <StandaloneFrontCoverstory
      link={{ href: data.link }}
      title={data.title}
      subtitle={data.subtitle}
      mediaUrl={device === 'mobile' ? data.mobilemediaurl : data.desktopmediaurl}
      logo={<ElleLogo />}
      options={{ $logo: { colors: data.logocolor_select } }}
    />
  );
};

export const standalone = StandaloneFrontCoverstory;

export default DefaultFrontCoverstory;
